import React, { useMemo, useState } from 'react';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import Currency from 'components/currency';
import { useComposeDescription } from 'hooks/use-compose-description';
import { t } from 'state/intl/translations';
import { CartEntryType } from 'utils/cart/types';

import {
  DescriptionRow,
  Divider,
  StyledLinkButton,
  Total,
  TotalLabelTitle,
  TotalsRow,
} from './modal-order-details.styled';

const ITEMS_DISPLAY_LENGTH = 2;

export const OrderItem = ({ cartEntry }: { cartEntry: IBackendCartEntries }) => {
  const { price, quantity } = cartEntry;
  const isOfferDiscount = cartEntry.type === CartEntryType.offerDiscount;
  const shouldShowPrice = price !== null && !isOfferDiscount;

  const [showAllModifiers, setShowAllModifiers] = useState(false);

  // Order item names are baked in as plain strings by the server at the time the order was
  // placed (resolved from whatever locale was active then) and never re-resolved afterward.
  // A guest who places an order in French, then switches the app to English, would otherwise
  // keep seeing French item names on this order forever. `useComposeDescription` already runs
  // the same legacy Sanity lookup (gated the same way for One Menu) to compute the modifier
  // descriptions below — reuse its resolved name instead of issuing a second, identical query.
  const { descriptions: cartEntryDescriptions, freshName } = useComposeDescription(cartEntry, {
    includeModifiers: true,
    includeQuantity: true,
  });
  const name = freshName ?? cartEntry.name;

  const modifiers = useMemo(() => {
    return cartEntryDescriptions?.slice(
      0,
      !showAllModifiers ? ITEMS_DISPLAY_LENGTH : cartEntryDescriptions.length
    );
  }, [cartEntryDescriptions, showAllModifiers]);

  const toggleModifiersList = () => {
    setShowAllModifiers(prevState => !prevState);
  };

  const toggleTextKey = showAllModifiers ? 'showLess' : 'showMore';
  const toggleText = t(toggleTextKey);

  return (
    <>
      <TotalsRow>
        <TotalLabelTitle>{name}</TotalLabelTitle>
        {shouldShowPrice && (
          <Total>
            <Currency amount={price} />
          </Total>
        )}
      </TotalsRow>

      <DescriptionRow mb="$3">
        {t('qty')}
        {quantity}
      </DescriptionRow>

      {modifiers?.map((descriptionItem, index) => (
        <DescriptionRow key={`descriptionItem-${index}`}>{descriptionItem}</DescriptionRow>
      ))}

      {cartEntryDescriptions.length > ITEMS_DISPLAY_LENGTH && (
        <StyledLinkButton onPress={toggleModifiersList}>{toggleText}</StyledLinkButton>
      )}

      <Divider />
    </>
  );
};
